import { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  Image as ImageIcon,
  Images,
  MapPin,
  MessageCircle,
  Plus,
  RefreshCcw,
  Search,
  User,
  X,
} from 'lucide-react';

import axiosClient from '../api/axiosClient';
import { getUser } from '../utils/auth';
import {
  formatDate,
  getAfterPhotos,
  getBeforePhotos,
  getFacadePhotos,
  getImageUrl,
} from '../utils/helpers';
import '../styles/merchBranding.css';

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
};

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Approuvé' },
  { value: 'rejected', label: 'Rejeté' },
];

const normalizeRegions = (regions) => {
  if (!Array.isArray(regions)) return [];
  return regions
    .map((item) => {
      if (typeof item === 'string') return item;
      return item.region || item.name || item.label || '';
    })
    .filter(Boolean);
};

const getPhotoItems = (branding, type) => {
  if (type === 'before') return getBeforePhotos(branding);
  if (type === 'after') return getAfterPhotos(branding);
  if (type === 'facade') return getFacadePhotos(branding);
  return [];
};

export default function MerchBrandingListPage() {
  const [brandings, setBrandings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isOutletModalOpen, setIsOutletModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [activeBranding, setActiveBranding] = useState(null);
  const [nomCampagne, setNomCampagne] = useState('');
  const [outletRegion, setOutletRegion] = useState('');
  const [outletId, setOutletId] = useState('');
  const [outletName, setOutletName] = useState('');
  const [dateVisite, setDateVisite] = useState(new Date().toISOString().slice(0, 10));
  const [remarque, setRemarque] = useState('');
  const [beforeFiles, setBeforeFiles] = useState([]);
  const [afterFiles, setAfterFiles] = useState([]);
  const [facadeFiles, setFacadeFiles] = useState([]);
  const [oldBeforePhotos, setOldBeforePhotos] = useState([]);
  const [oldAfterPhotos, setOldAfterPhotos] = useState([]);
  const [oldFacadePhotos, setOldFacadePhotos] = useState([]);
  const [regions, setRegions] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const totalBrandings = pagination?.total ?? brandings.length;

  const user = getUser();
  const isClient = user?.role === 'client';

  const statusCounts = useMemo(() => {
    return brandings.reduce(
      (acc, item) => {
        const status = item.status || item.etat || 'pending';
        if (status === 'approved') acc.approved += 1;
        else if (status === 'rejected') acc.rejected += 1;
        else acc.pending += 1;
        return acc;
      },
      { pending: 0, approved: 0, rejected: 0 }
    );
  }, [brandings]);

  const buildParams = (page = 1) => {
    const params = new URLSearchParams();
    params.append('page', String(page));

    if (searchTerm.trim()) {
      params.append('search', searchTerm.trim());
    }

    if (selectedStatus) {
      params.append('status', selectedStatus);
    }

    if (dateFrom) {
      params.append('date_from', dateFrom);
    }

    if (dateTo) {
      params.append('date_to', dateTo);
    }

    return params.toString();
  };

  const fetchBrandings = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }

      setErrorMessage('');
      const response = await axiosClient.get(`/brandings?${buildParams(page)}`);
      const paginatedData = response.data?.data;
      const list = paginatedData?.data || [];

      setBrandings(list);
      setPagination(paginatedData);
      setCurrentPage(paginatedData?.current_page || page);
    } catch (error) {
      console.error('Fetch brandings error', error);
      setErrorMessage(
        error?.response?.data?.message || 'Impossible de charger les visites Merch.'
      );
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axiosClient.get('/outlets-regions');
      setRegions(normalizeRegions(response.data?.data || response.data || []));
    } catch (error) {
      console.error('Fetch regions error', error);
    }
  };

  const fetchOutlets = async (region) => {
    if (!region) {
      setOutlets([]);
      return;
    }

    try {
      const response = await axiosClient.get(
        `/outlets-by-region?region=${encodeURIComponent(region)}`
      );
      setOutlets(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Fetch outlets error', error);
    }
  };

  useEffect(() => {
    fetchBrandings(1);
    fetchRegions();
  }, []);

  useEffect(() => {
    if (outletRegion) {
      fetchOutlets(outletRegion);
    }
  }, [outletRegion]);

  const resetForm = () => {
    setFormMode('create');
    setActiveBranding(null);
    setNomCampagne('');
    setOutletRegion('');
    setOutletId('');
    setOutletName('');
    setDateVisite(new Date().toISOString().slice(0, 10));
    setRemarque('');
    setBeforeFiles([]);
    setAfterFiles([]);
    setFacadeFiles([]);
    setOldBeforePhotos([]);
    setOldAfterPhotos([]);
    setOldFacadePhotos([]);
    setFormError('');
  };

  const openForm = (branding = null) => {
    if (!branding) {
      resetForm();
      setIsFormOpen(true);
      return;
    }

    setFormMode('edit');
    setActiveBranding(branding);
    setNomCampagne(branding.nomCampagne || '');
    setOutletRegion(branding.outlet?.region || branding.region || '');
    setOutletId(branding.outlet?.id || '');
    setOutletName(branding.outlet?.name || '');
    setDateVisite(
      branding.date_visite || branding.dateVisite || new Date().toISOString().slice(0, 10)
    );
    setRemarque(branding.remarque || '');
    setBeforeFiles([]);
    setAfterFiles([]);
    setFacadeFiles([]);
    setOldBeforePhotos(getBeforePhotos(branding));
    setOldAfterPhotos(getAfterPhotos(branding));
    setOldFacadePhotos(getFacadePhotos(branding));
    setFormError('');
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const handleOpenOutletModal = () => {
    setIsOutletModalOpen(true);
  };

  const handleCloseOutletModal = () => {
    setIsOutletModalOpen(false);
  };

  const handleSelectRegion = (region) => {
    setOutletRegion(region);
    setOutletId('');
    setOutletName('');
  };

  const handleSelectOutlet = (outlet) => {
    setOutletId(outlet.id);
    setOutletName(outlet.name || outlet.label || '');
    setIsOutletModalOpen(false);
  };

  const handleFileChange = (event, setter) => {
    const files = Array.from(event.target.files || []);
    setter((prev) => [...prev, ...files]);
    event.target.value = null;
  };

  const removeFile = (index, setter) => {
    setter((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const getFilePreviews = (files) => {
    return files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      url: URL.createObjectURL(file),
      name: file.name,
    }));
  };

  const PhotoUploadBlock = ({
    title,
    files,
    oldFiles,
    inputId,
    onChange,
    onRemove,
    onRemoveOld,
  }) => {
    const previews = getFilePreviews(files);

    return (
      <div className="photo-upload-card">
        <div className="photo-upload-card-header">
          <div>
            <p className="photo-upload-title">{title}</p>
            <p className="photo-upload-description">Cliquez pour ajouter des photos</p>
          </div>
          <span className="photo-upload-badge">{files.length + oldFiles.length}</span>
        </div>

        <label htmlFor={inputId} className="photo-upload-input">
          <div className="photo-upload-placeholder">
            <ImageIcon size={22} />
            <p>Ajouter des images</p>
          </div>
        </label>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          onChange={onChange}
        />

        <div className="preview-strip">
          {oldFiles.map((photo, index) => (
            <div key={photo.id || `old-${index}`} className="preview-card">
              <img src={getImageUrl(photo.path)} alt={`${title} existant`} />
              <button
                type="button"
                className="preview-delete"
                onClick={() => onRemoveOld(index)}
              >
                <X size={14} />
              </button>
            </div>
          ))}

          {previews.map((file, index) => (
            <div key={file.id} className="preview-card">
              <img src={file.url} alt={file.name} />
              <button
                type="button"
                className="preview-delete"
                onClick={() => onRemove(index)}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const validateForm = () => {
    if (!outletId) {
      setFormError('Veuillez sélectionner un outlet.');
      return false;
    }

    if (!nomCampagne.trim()) {
      setFormError('Veuillez saisir le nom de la campagne.');
      return false;
    }

    if (oldBeforePhotos.length + beforeFiles.length === 0) {
      setFormError('Au moins une photo avant est requise.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    const formData = new FormData();
    formData.append('outlet_id', outletId);
    formData.append('nomCampagne', nomCampagne);
    formData.append('remarque', remarque);
    formData.append('date_visite', dateVisite);

    beforeFiles.forEach((file) => formData.append('photo_before[]', file));
    afterFiles.forEach((file) => formData.append('photo_after[]', file));
    facadeFiles.forEach((file) => formData.append('photo_facade[]', file));

    if (formMode === 'edit' && activeBranding?.id) {
      formData.append('_method', 'PUT');
    }

    setFormLoading(true);

    try {
      if (formMode === 'edit' && activeBranding?.id) {
        await axiosClient.post(`/brandings/${activeBranding.id}`, formData);
      } else {
        await axiosClient.post('/brandings', formData);
      }

      alert('Visite merch enregistrée avec succès.');
      closeForm();
      fetchBrandings(currentPage);
    } catch (error) {
      console.error('Submit branding error', error);
      setFormError(
        error?.response?.data?.message || 'Une erreur est survenue lors de l’envoi.'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const openPhotoPreview = (photo, label) => {
    setPreviewPhoto({ url: getImageUrl(photo.path || photo), label });
  };

  const closePhotoPreview = () => {
    setPreviewPhoto(null);
  };

  const goToPreviousPage = () => {
    if (!pagination || currentPage <= 1) return;
    fetchBrandings(currentPage - 1);
  };

  const goToNextPage = () => {
    if (!pagination || currentPage >= pagination.last_page) return;
    fetchBrandings(currentPage + 1);
  };

  const applyFilters = () => {
    fetchBrandings(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setDateFrom('');
    setDateTo('');
    fetchBrandings(1);
  };

  const statusSummary = useMemo(
    () => [
      { label: 'Total', value: totalBrandings },
      { label: 'En attente', value: statusCounts.pending },
      { label: 'Approuvées', value: statusCounts.approved },
      { label: 'Rejetées', value: statusCounts.rejected },
    ],
    [totalBrandings, statusCounts]
  );

  const currentDate = new Date().toISOString().slice(0, 10);

  return (
    <div className="merch-page">
      <section className="branding-top-card merch-top-card">
        <div>
          <p className="page-kicker">{isClient ? 'Espace Client' : 'Visites Merch'}</p>
          <h1>Visites Merch</h1>
          <p>Ajoutez et suivez les visites merchandising.</p>
        </div>

        <div className="branding-total-box merch-total-box">
          <span>{totalBrandings}</span>
          <small>Total des visites</small>
        </div>
      </section>

      <section className="filter-card merch-filter-card">
        <div className="filter-header">
          <div>
            <h2>Actions et filtres</h2>
            <p>Recherche campagne/outlet, statut et plages de date.</p>
          </div>

          <button
            type="button"
            className="refresh-btn"
            onClick={() => fetchBrandings(currentPage)}
            disabled={pageLoading}
          >
            <RefreshCcw size={18} />
            Actualiser
          </button>
        </div>

        <div className="filters-grid">
          <label className="filter-field">
            <span>Campagne / Outlet</span>
            <div className="filter-input-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Rechercher par campagne ou outlet"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
          </label>

          <label className="filter-field">
            <span>Statut</span>
            <div className="filter-input-box">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="filter-field">
            <span>Date début</span>
            <div className="filter-input-box">
              <Calendar size={18} />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={currentDate}
              />
            </div>
          </label>

          <label className="filter-field">
            <span>Date fin</span>
            <div className="filter-input-box">
              <Calendar size={18} />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                max={currentDate}
              />
            </div>
          </label>
        </div>

        <div className="filter-actions">
          <button type="button" className="apply-filter-btn" onClick={applyFilters}>
            <Search size={18} />
            Appliquer
          </button>
          <button type="button" className="reset-filter-btn" onClick={resetFilters}>
            <RefreshCcw size={18} />
            Réinitialiser
          </button>
          {!isClient && (
            <button type="button" className="primary-button" onClick={() => openForm()}>
              <Plus size={18} /> Ajouter une visite
            </button>
          )}
        </div>
      </section>

      <section className="merch-stats-grid">
        {statusSummary.map((item) => (
          <div key={item.label} className="merch-stat-card">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </section>

      {loading ? (
        <div className="branding-loading merch-loading">
          <span className="large-loader" />
          <p>Chargement des visites merch...</p>
        </div>
      ) : errorMessage ? (
        <div className="branding-error merch-error-state">{errorMessage}</div>
      ) : brandings.length === 0 ? (
        <section className="empty-brandings merch-empty-state">
          <ImageIcon size={54} />
          <h3>Aucune visite Merch trouvée</h3>
          <p>Essayez d’ajuster les filtres ou ajoutez une nouvelle visite.</p>
        </section>
      ) : (
        <section className="merch-list">
          {brandings.map((branding) => {
            const beforePhotos = getPhotoItems(branding, 'before');
            const afterPhotos = getPhotoItems(branding, 'after');
            const facadePhotos = getPhotoItems(branding, 'facade');
            const status = branding.status || branding.etat || 'pending';

            return (
              <article className="merch-card" key={branding.id}>
                <div className="branding-card-header">
                  <div className="branding-icon-box">
                    <ImageIcon size={26} />
                  </div>

                  <div className="branding-main-info">
                    <h2>{branding.outlet?.name || 'Outlet non défini'}</h2>
                    <span className="branding-campaign">
                      {branding.nomCampagne || 'Campagne non spécifiée'}
                    </span>

                    <div className="branding-meta">
                      <span>
                        <MapPin size={14} />
                        {branding.outlet?.region || branding.region || 'Région non définie'}
                      </span>
                      <span>
                        <Calendar size={14} />
                        {formatDate(branding.date_visite || branding.dateVisite || branding.date)}
                      </span>
                      {branding.user && (
                        <span>
                          <User size={14} />
                          {branding.user?.name || branding.user?.prenom || 'Utilisateur'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <span className={`status-badge status-${status}`}>
                      {STATUS_LABELS[status] || status}
                    </span>
                  </div>
                </div>

                <div className="albums-grid">
                  <div className="photo-album-card">
                    <div className="photo-album-header">
                      <div className="photo-album-title">
                        <div className="photo-album-icon">
                          <Images size={18} />
                        </div>
                        <div>
                          <h3>Photos façade</h3>
                          <p>{facadePhotos.length} image(s)</p>
                        </div>
                      </div>
                      <span className="photo-count-badge">{facadePhotos.length}</span>
                    </div>
                    {facadePhotos.length > 0 ? (
                      <div className="photo-grid">
                        {facadePhotos.map((photo, photoIndex) => (
                          <button
                            type="button"
                            key={photo.id || `${branding.id}-facade-${photoIndex}`}
                            className="photo-tile"
                            onClick={() => openPhotoPreview(photo, 'Façade')}
                          >
                            <img src={getImageUrl(photo.path)} alt={`Façade ${photoIndex + 1}`} />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-photo-album">
                        <ImageIcon size={24} />
                        Aucune photo façade
                      </div>
                    )}
                  </div>

                  <div className="photo-album-card">
                    <div className="photo-album-header">
                      <div className="photo-album-title">
                        <div className="photo-album-icon">
                          <ImageIcon size={18} />
                        </div>
                        <div>
                          <h3>Photos avant</h3>
                          <p>{beforePhotos.length} image(s)</p>
                        </div>
                      </div>
                      <span className="photo-count-badge">{beforePhotos.length}</span>
                    </div>
                    {beforePhotos.length > 0 ? (
                      <div className="photo-grid">
                        {beforePhotos.map((photo, photoIndex) => (
                          <button
                            type="button"
                            key={photo.id || `${branding.id}-before-${photoIndex}`}
                            className="photo-tile"
                            onClick={() => openPhotoPreview(photo, 'Avant')}
                          >
                            <img src={getImageUrl(photo.path)} alt={`Avant ${photoIndex + 1}`} />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-photo-album">
                        <ImageIcon size={24} />
                        Aucune photo avant
                      </div>
                    )}
                  </div>

                  <div className="photo-album-card">
                    <div className="photo-album-header">
                      <div className="photo-album-title">
                        <div className="photo-album-icon">
                          <ImageIcon size={18} />
                        </div>
                        <div>
                          <h3>Photos après</h3>
                          <p>{afterPhotos.length} image(s)</p>
                        </div>
                      </div>
                      <span className="photo-count-badge">{afterPhotos.length}</span>
                    </div>
                    {afterPhotos.length > 0 ? (
                      <div className="photo-grid">
                        {afterPhotos.map((photo, photoIndex) => (
                          <button
                            type="button"
                            key={photo.id || `${branding.id}-after-${photoIndex}`}
                            className="photo-tile"
                            onClick={() => openPhotoPreview(photo, 'Après')}
                          >
                            <img src={getImageUrl(photo.path)} alt={`Après ${photoIndex + 1}`} />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-photo-album">
                        <ImageIcon size={24} />
                        Aucune photo après
                      </div>
                    )}
                  </div>
                </div>

                <div className="branding-remark">
                  <MessageCircle size={18} />
                  <span>{branding.remarque || 'Aucune remarque'}</span>
                </div>

                <div className="merch-card-actions">
                  {status !== 'approved' && !isClient ? (
                    <button
                      type="button"
                      className="secondary-button merch-edit-button"
                      onClick={() => openForm(branding)}
                    >
                      <Edit size={16} /> Modifier
                    </button>
                  ) : (
                    <div className="merch-approved-message">
                      <CheckCircle size={16} /> Visite approuvée, modification bloquée.
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {pagination && pagination.last_page > 1 && (
        <div className="merch-pagination-card">
          <div className="merch-pagination-info">
            {pagination.from || 0} - {pagination.to || 0} sur {pagination.total || 0}
          </div>

          <div className="pagination-buttons">
            <button
              type="button"
              className="page-action"
              disabled={currentPage <= 1 || pageLoading}
              onClick={goToPreviousPage}
            >
              <ChevronLeft size={16} /> Précédent
            </button>
            <button
              type="button"
              className="page-action"
              disabled={currentPage >= pagination.last_page || pageLoading}
              onClick={goToNextPage}
            >
              Suivant <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="form-modal-overlay" role="dialog" aria-modal="true">
          <div className="form-modal-card">
            <div className="form-modal-header">
              <div>
                <span className="page-kicker">{formMode === 'edit' ? 'Modifier une visite' : 'Nouvelle visite'}</span>
                <h2>{formMode === 'edit' ? 'Modifier la visite Merch' : 'Ajouter une visite Merch'}</h2>
              </div>
              <button type="button" className="icon-button" onClick={closeForm}>
                <X size={20} />
              </button>
            </div>

            <form className="merch-form-card" onSubmit={handleSubmit}>
              <div className="form-row">
                <label className="form-group">
                  <span>Nom de campagne</span>
                  <input
                    type="text"
                    value={nomCampagne}
                    onChange={(e) => setNomCampagne(e.target.value)}
                    placeholder="Entrez le nom de la campagne"
                  />
                </label>

                <label className="form-group">
                  <span>Date de visite</span>
                  <input
                    type="date"
                    value={dateVisite}
                    onChange={(e) => setDateVisite(e.target.value)}
                  />
                </label>
              </div>

              <div className="form-group">
                <span>Outlet</span>
                <button type="button" className="select-outlet-button" onClick={handleOpenOutletModal}>
                  <div>
                    <strong>{outletName || 'Choisir une région et un outlet'}</strong>
                    <small>{outletRegion || 'Région'}</small>
                  </div>
                  <Plus size={16} />
                </button>
              </div>

              <div className="form-group">
                <span>Remarque</span>
                <textarea
                  rows={4}
                  value={remarque}
                  onChange={(e) => setRemarque(e.target.value)}
                  placeholder="Ajouter une remarque"
                />
              </div>

              <div className="upload-section">
                <div className="upload-block">
                  <span>Photos façade</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileChange(e, setFacadeFiles)}
                  />
                  <div className="preview-strip">
                    {oldFacadePhotos.map((photo) => (
                      <div key={photo.id} className="preview-card">
                        <img src={getImageUrl(photo.path)} alt="Façade existante" />
                      </div>
                    ))}
                    {getFilePreviews(facadeFiles).map((file, index) => (
                      <div className="preview-card" key={file.id}>
                        <img src={file.url} alt={file.name} />
                        <button
                          type="button"
                          className="preview-delete"
                          onClick={() => removeFile(index, setFacadeFiles)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="upload-block">
                  <span>Photos avant</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileChange(e, setBeforeFiles)}
                  />
                  <div className="preview-strip">
                    {oldBeforePhotos.map((photo) => (
                      <div key={photo.id} className="preview-card">
                        <img src={getImageUrl(photo.path)} alt="Avant existant" />
                      </div>
                    ))}
                    {getFilePreviews(beforeFiles).map((file, index) => (
                      <div className="preview-card" key={file.id}>
                        <img src={file.url} alt={file.name} />
                        <button
                          type="button"
                          className="preview-delete"
                          onClick={() => removeFile(index, setBeforeFiles)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="upload-block">
                  <span>Photos après</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileChange(e, setAfterFiles)}
                  />
                  <div className="preview-strip">
                    {oldAfterPhotos.map((photo) => (
                      <div key={photo.id} className="preview-card">
                        <img src={getImageUrl(photo.path)} alt="Après existant" />
                      </div>
                    ))}
                    {getFilePreviews(afterFiles).map((file, index) => (
                      <div className="preview-card" key={file.id}>
                        <img src={file.url} alt={file.name} />
                        <button
                          type="button"
                          className="preview-delete"
                          onClick={() => removeFile(index, setAfterFiles)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {formError && <div className="form-error-message">{formError}</div>}

              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={closeForm}>
                  Annuler
                </button>
                <button type="submit" className="primary-button" disabled={formLoading}>
                  {formLoading ? 'Enregistrement...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isOutletModalOpen && (
        <div className="select-modal-overlay" role="dialog" aria-modal="true">
          <div className="select-modal-card">
            <div className="select-modal-header">
              <div>
                <span className="page-kicker">Choix Outlet</span>
                <h2>Sélectionnez une région puis un outlet</h2>
              </div>
              <button type="button" className="icon-button" onClick={handleCloseOutletModal}>
                <X size={18} />
              </button>
            </div>

            <div className="select-modal-body">
              <div className="select-section">
                <h3>Régions</h3>
                <div className="region-list">
                  {regions.map((region) => (
                    <button
                      type="button"
                      key={region}
                      className={`region-item ${region === outletRegion ? 'selected' : ''}`}
                      onClick={() => handleSelectRegion(region)}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>

              <div className="select-section">
                <h3>Outlets</h3>
                {outletRegion ? (
                  <div className="outlet-list">
                    {outlets.length === 0 ? (
                      <p>Aucun outlet disponible pour cette région.</p>
                    ) : (
                      outlets.map((outlet) => (
                        <button
                          type="button"
                          key={outlet.id}
                          className={`outlet-item ${outlet.id === outletId ? 'selected' : ''}`}
                          onClick={() => handleSelectOutlet(outlet)}
                        >
                          <strong>{outlet.name || outlet.label || 'Outlet non défini'}</strong>
                          <span>{outlet.address || outlet.region || ''}</span>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  <p>Sélectionnez une région pour afficher les outlets.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewPhoto && (
        <div className="photo-modal-overlay" role="dialog" aria-modal="true">
          <div className="photo-modal-content">
            <div className="photo-modal-header">
              <div>
                <span className="photo-modal-kicker">Aperçu photo</span>
                <h3>{previewPhoto.label}</h3>
              </div>
              <button type="button" className="icon-button" onClick={closePhotoPreview}>
                <X size={20} />
              </button>
            </div>
            <div className="photo-modal-body">
              <img src={previewPhoto.url} alt={previewPhoto.label} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
